//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import React, { useCallback, useRef, useEffect, useMemo } from "react";
import styled from "styled-components";

import {Alert} from '@mui/material';
import {Snackbar } from '@mui/material';
import SunEditor from "suneditor-react";
import { SPANISH_ITEM_LOWER, SPANISH_ITEM_UPPER } from "utils/config/reference";
import {
  blockquote,
  align,
  font,
  fontSize,
  fontColor,
  hiliteColor,
  horizontalRule,
  list,
  table,
  formatBlock,
  lineHeight,
  template,
  paragraphStyle,
  textStyle,
  link,
  image,
} from "suneditor/src/plugins";

//  -- Constants ----------------------
import * as Sentry from "@sentry/react";
import { useAlert } from "./UI/alertSakeBar/alertProvider";
import {
  getHistoryEntryContent,
  syncEditorAfterHistoryApply,
  syncEditorPlaceholder,
} from "utils/studentEditorHistoryUtils";
import { useCurrentUser } from "store/users/selectors";
import {
  CANONICAL_EMPTY_NOTE_HTML,
  isHtmlNoteVisuallyEmpty,
} from "utils/methods/htmlNoteUtils";

const BASE_BUTTON_LIST = [
  ["font", "fontSize"],
  ["bold", "underline", "italic"],
  [
    {
      name: "SPANISH_ITEM_LOWER",
      dataCommand: "SPANISH_ITEM_LOWER",
      buttonClass: "",
      title: `${SPANISH_ITEM_LOWER[0]}`,
      dataDisplay: "submenu",
      innerHTML: `<span>${SPANISH_ITEM_LOWER[0]}</span>`,
    },
  ],
  [
    {
      name: "SPANISH_ITEM_UPPER",
      dataCommand: "SPANISH_ITEM_UPPER",
      buttonClass: "",
      title: `${SPANISH_ITEM_UPPER[0]}`,
      dataDisplay: "submenu",
      innerHTML: `<span>${SPANISH_ITEM_UPPER[0]}</span>`,
    },
  ],
];

const STUDENT_UNDO_ICON =
  '<svg class="se-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/></svg>';

const STUDENT_REDO_ICON =
  '<svg class="se-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.06-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/></svg>';

const ACCENT_ROW_SIZE = 3;

const pushEditorChange = (editorRef) => {
  const editor = editorRef.current;
  if (editor?.getContents && editor.onChange) {
    editor.onChange(editor.getContents(true));
  }
};

const getAccentGridSubmenuHtml = (items, buttonClassName) => {
  const rows = [];
  for (let index = 0; index < items.length; index += ACCENT_ROW_SIZE) {
    const rowItems = items.slice(index, index + ACCENT_ROW_SIZE);
    rows.push(`<div style="display:grid;grid-template-columns:repeat(${rowItems.length}, minmax(28px, auto));gap:6px;">${rowItems
      .map(
        (item) =>
          `<button type="button" class="${buttonClassName}" value="${item}" style="min-width:28px;height:28px;border:1px solid #d9d9d9;border-radius:6px;background:#fff;cursor:pointer;">${item}</button>`
      )
      .join("")}</div>`);
  }

  return `<div class="se-list-inner se-list-font-size" style="width:auto;min-width:126px;"><div style="display:flex;flex-direction:column;gap:6px;padding:4px;">${rows.join("")}</div></div>`;
};

const buildAccentPlugin = (editorRef, config) => ({
  name: config.name,
  display: "submenu",
  add: function (core, targetElement) {
    let listDiv = this.setSubmenu.call(core);
    var self = this;
    listDiv.querySelectorAll(config.buttonSelector).forEach(function (btn) {
      btn.addEventListener("click", self.onClick.bind(core));
    });
    core.initMenuTarget(this.name, targetElement, listDiv);
  },

  setSubmenu: function () {
    const listDiv = this.util.createElement("DIV");
    listDiv.className = config.submenuClassName;
    listDiv.innerHTML = getAccentGridSubmenuHtml(config.items, config.buttonClassName);
    return listDiv;
  },
  onClick: function (e) {
    e.preventDefault();
    const value = e.target.value;
    const node = this.util.createElement("span");
    this.util.addClass(node, "se-custom-tag");
    node.textContent = value;
    this.insertNode(node);
    const zeroWidthSpace = this.util.createTextNode(this.util.zeroWidthSpace);
    node.parentNode.insertBefore(zeroWidthSpace, node.nextSibling);
    this.submenuOff();
    pushEditorChange(editorRef);
  },
});
//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------
const Container = styled.div`
  .sun-editor,
  .sun-editor .se-wrapper,
  .sun-editor .se-container,
  .sun-editor .se-toolbar-container,
  .sun-editor .se-toolbar {
    overflow: visible !important;
  }

  .sun-editor,
  .sun-editor .se-wrapper,
  .sun-editor .se-container,
  .sun-editor .se-wrapper-wysiwyg {
    position: relative;
    z-index: 1;
  }

  .sun-editor .se-toolbar,
  .sun-editor .se-toolbar-container {
    position: relative;
    z-index: 20;
  }

  .sun-editor .se-submenu,
  .sun-editor .se-list-layer {
    position: absolute !important;
    z-index: 10000 !important;
  }
`;
//  ----------------------------------------------------------------------------
//  Helpers
//  ----------------------------------------------------------------------------
//  ----------------------------------------------------------------------------
//  Container
//  ----------------------------------------------------------------------------
//
//  Not found container
//
//  -- Description
//  Failed route catch container
//
//  -- Props
//  ...
//
//  -- State
//  ...
//
//  -- Methods
//  ...
//
//  -- Usage
//  ...
//

const NotesTextEditor = ({
  handleChange,
  value,
  autoFocus = true,
  disabled,
  page = "",
  role = "",
}) => {
  const currentUser = useCurrentUser();
  const showAlert = useAlert();
  const editorRef = useRef();
  const isStudent = currentUser?.role === "student";
  const [open, setOpen] = React.useState(false);
  const studentHistoryRef = useRef({ stack: [], index: 0 });
  const studentHistoryDelayRef = useRef(null);
  const isApplyingStudentHistoryRef = useRef(false);
  const studentUndoHandlerRef = useRef(() => {});
  const studentRedoHandlerRef = useRef(() => {});
  const pasteBlockRef = useRef({
    lastPasteTime: 0,
    pasteCount: 0,
    maxPasteAttempts: 3,
    resetTimeout: 1000,
  });

  const showPasteBlockedAlert = useCallback(() => {
    try {
      if (window.location.origin.includes("localhost")) return;
      const now = Date.now();
      const pasteBlock = pasteBlockRef.current;

      if (now - pasteBlock.lastPasteTime < pasteBlock.resetTimeout) {
        pasteBlock.pasteCount += 1;
      } else {
        pasteBlock.pasteCount = 1;
      }
      pasteBlock.lastPasteTime = now;
      showAlert("Paste not allowed", "error");

      if (pasteBlock.pasteCount > pasteBlock.maxPasteAttempts) {
        showAlert("Paste not allowed", "error");
      }
    } catch (error) {
      Sentry.captureException(`Error in showPasteBlockedAlert: ${error}`);
    }
  }, [showAlert]);

  const pushStudentHistory = useCallback((content) => {
    if (isApplyingStudentHistoryRef.current) return;
    const history = studentHistoryRef.current;
    if (getHistoryEntryContent(history.stack[history.index]) === content) return;

    history.stack = history.stack.slice(0, history.index + 1);
    history.stack.push(content);
    history.index = history.stack.length - 1;

    if (history.stack.length > 100) {
      history.stack.shift();
      history.index -= 1;
    }
  }, []);

  const schedulePushStudentHistory = useCallback(
    (content) => {
      if (studentHistoryDelayRef.current) {
        clearTimeout(studentHistoryDelayRef.current);
      }
      studentHistoryDelayRef.current = setTimeout(() => {
        studentHistoryDelayRef.current = null;
        pushStudentHistory(content);
      }, 400);
    },
    [pushStudentHistory]
  );

  const applyStudentHistoryContent = useCallback(
    (historyItem) => {
      const editor = editorRef.current;
      if (!editor?.core) return;

      const content = getHistoryEntryContent(historyItem);

      isApplyingStudentHistoryRef.current = true;
      const { core } = editor;
      const convertValue =
        content == null ? "" : core.convertContentsForEditor(content, null, null);
      core.context.element.wysiwyg.innerHTML = convertValue;
      core._resetComponents();
      syncEditorAfterHistoryApply(core, content);

      const cleanValue =
        typeof content === "string" ? content.replace(/<img[^>]*>/g, "") : content;
      handleChange(cleanValue);
      isApplyingStudentHistoryRef.current = false;
    },
    [handleChange]
  );

  const studentUndo = useCallback(() => {
    const history = studentHistoryRef.current;
    if (history.index <= 0) return;
    history.index -= 1;
    applyStudentHistoryContent(history.stack[history.index]);
  }, [applyStudentHistoryContent]);

  const studentRedo = useCallback(() => {
    const history = studentHistoryRef.current;
    if (history.index >= history.stack.length - 1) return;
    history.index += 1;
    applyStudentHistoryContent(history.stack[history.index]);
  }, [applyStudentHistoryContent]);

  studentUndoHandlerRef.current = studentUndo;
  studentRedoHandlerRef.current = studentRedo;

  const studentUndoPlugin = useMemo(
    () => ({
      name: "studentUndo",
      display: "command",
      title: "Undo",
      innerHTML: STUDENT_UNDO_ICON,
      add: function (core, targetElement) {
        core.context.studentUndo = { targetButton: targetElement };
      },
      action: function studentUndoAction() {
        studentUndoHandlerRef.current();
      },
    }),
    []
  );

  const studentRedoPlugin = useMemo(
    () => ({
      name: "studentRedo",
      display: "command",
      title: "Redo",
      innerHTML: STUDENT_REDO_ICON,
      add: function (core, targetElement) {
        core.context.studentRedo = { targetButton: targetElement };
      },
      action: function studentRedoAction() {
        studentRedoHandlerRef.current();
      },
    }),
    []
  );

  const editorButtonList = useMemo(
    () => [
      ...BASE_BUTTON_LIST,
      isStudent ? ["studentUndo", "studentRedo"] : ["undo", "redo"],
    ],
    [isStudent]
  );

  const editorPlugins = useMemo(
    () => [
      blockquote,
      align,
      font,
      fontColor,
      fontSize,
      formatBlock,
      hiliteColor,
      horizontalRule,
      lineHeight,
      list,
      paragraphStyle,
      table,
      template,
      textStyle,
      image,
      link,
      buildAccentPlugin(editorRef, {
        name: "SPANISH_ITEM_LOWER",
        items: SPANISH_ITEM_LOWER,
        buttonClassName: "se-btn-list-lower",
        buttonSelector: ".se-btn-list-lower",
        submenuClassName: "se-submenu se-list-layer bubbleNotesEditor 12",
      }),
      buildAccentPlugin(editorRef, {
        name: "SPANISH_ITEM_UPPER",
        items: SPANISH_ITEM_UPPER,
        buttonClassName: "se-btn-list-upper",
        buttonSelector: ".se-btn-list-upper",
        submenuClassName: "se-submenu se-list-layer bubbleNotesEditor 23",
      }),
      ...(isStudent ? [studentUndoPlugin, studentRedoPlugin] : []),
    ],
    [isStudent, studentUndoPlugin, studentRedoPlugin]
  );

  const getSunEditorInstance = useCallback(
    (sunEditor) => {
      editorRef.current = sunEditor;
      if (isStudent) {
        const initial = sunEditor.getContents(true) || value || "";
        studentHistoryRef.current = { stack: [initial], index: 0 };
      }
      syncEditorPlaceholder(sunEditor.core, sunEditor.getContents(true) || value || "");
    },
    [isStudent, value]
  );

  useEffect(() => {
    return () => {
      if (studentHistoryDelayRef.current) {
        clearTimeout(studentHistoryDelayRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (
      editorRef.current &&
      editorRef.current.getContents(true) == "<p><br></p>" &&
      value
    ) {
      editorRef.current.setContents(value);
      syncEditorPlaceholder(editorRef.current.core, value);
    }
  }, [value]);
  const handleClose = () => {
    setOpen(false);
  };
  const handleDrop = (event) => {
    if (isStudent) {
      showPasteBlockedAlert();
      return false;
    }
  };
  const onPasteItem = (item) => {
    setOpen(true);
    const currentContent = editorRef.current.getContents(true);
    setTimeout(() => {
      editorRef.current.setContents(currentContent);
    }, 200);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      try {
        if (
          (event.ctrlKey || event.metaKey) &&
          (event.key === 'v' || event.key === 'V') &&
          page === 'ebookAndBubble'
        ) {
          // Prevent Ctrl+V or Cmd+V
          event.preventDefault();
          showAlert('Pasting is not allowed!', 'error');
        }
      } catch (error) {
        Sentry.captureException(`238 Error in handleKeyDown: ${error}`);
        console.log(error);
      }
    };

    const handlePaste = (e) => {
      try {
        if (currentUser && currentUser?.role === 'teacher') {
          return;
        }
        if (page === 'ebookAndBubble') {
          e.preventDefault();
          showAlert('Pasting is not allowed!', 'error');
        }
      } catch (error) {
        Sentry.captureException(`239 Error in handlePaste: ${error}`);
        console.log(error);
      }
    };

    // Attach event listeners
    if (currentUser && currentUser?.role === 'student') {
      document.addEventListener('paste', handlePaste);
      document.addEventListener('keydown', handleKeyDown);
    }

    // Cleanup event listeners on component unmount
    return () => {
      if (currentUser && currentUser?.role === 'student') {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('paste', handlePaste);
      }
    };
  }, [page,currentUser]);
  
  const handleEditorChange = useCallback(
    (nextValue) => {
      const cleanValue =
        typeof nextValue === "string" ? nextValue.replace(/<img[^>]*>/g, "") : nextValue;
      const normalizedValue =
        typeof cleanValue === "string" && isHtmlNoteVisuallyEmpty(cleanValue)
          ? CANONICAL_EMPTY_NOTE_HTML
          : cleanValue;
      handleChange(normalizedValue);
      if (isStudent) {
        schedulePushStudentHistory(normalizedValue);
      }
    },
    [handleChange, isStudent, schedulePushStudentHistory]
  );

  const handleStudentKeyDown = useCallback(
    (event) => {
      if (!isStudent) return;
      const isMod = event.ctrlKey || event.metaKey;
      if (!isMod) return;

      if (event.key === "z" || event.key === "Z") {
        event.preventDefault();
        if (event.shiftKey) {
          studentRedo();
        } else {
          studentUndo();
        }
      }

      if (event.key === "y" || event.key === "Y") {
        event.preventDefault();
        studentRedo();
      }
    },
    [isStudent, studentRedo, studentUndo]
  );

  const handleEditorPaste = useCallback(() => {
    if (isStudent) {
      showPasteBlockedAlert();
      return false;
    }
    return undefined;
  }, [isStudent, showPasteBlockedAlert]);

  return (
    <Container
      className={`${page === "ebookAndBubble" ? "ebookAndBubble" : ""}`}
    >
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        style={{ width: "100%" }}
      >
        <Alert onClose={handleClose} severity="error">
          <strong>ALERT:</strong> Paste not allowed.
        </Alert>
      </Snackbar>
      <SunEditor
        getSunEditorInstance={getSunEditorInstance}
        setAllPlugins={true}
        autoFocus={autoFocus}
        disable={disabled}
        disableToolbar={disabled}
        onDrop={handleDrop}
        onKeyDown={handleStudentKeyDown}
        placeholder="Please type here..."
        setOptions={{
          width: "100%",
          showPathLabel: false,
          buttonList: editorButtonList,
          shortcutsDisable: isStudent ? ["undo", "redo"] : [],
          plugins: editorPlugins,
        }}
        setDefaultStyle={"text-align: left, overflow: auto"}
        onPaste={handleEditorPaste}
        onChange={handleEditorChange}
        defaultValue={value}
      />
    </Container>
  );
};


export default NotesTextEditor;
